"use client";

import { motion, useAnimation } from "framer-motion";
import { useEffect, useState } from "react";
import Image from "@/components/common/image/image";
import { addQueryParams } from "@/utilities/url";
import useOnScreen from "@/hooks/useOnScreen";
import { ImageLoading } from "@/utilities/imageLoading";

interface AnimatedMobileShowcaseProps {
  backgroundSrc: string;
  leftMobileSrc: string;
  rightMobileSrc: string;
  className?: string;
}

const AnimatedMobileShowcase = ({
  backgroundSrc,
  leftMobileSrc,
  rightMobileSrc,
  className = "",
}: AnimatedMobileShowcaseProps) => {
  const controls = useAnimation();
  const { ref, inView } = useOnScreen();
  const [screenSize, setScreenSize] = useState("mobile");

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setScreenSize("desktop");
      } else {
        setScreenSize("mobile");
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Set the initial state based on the window size

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <div
      ref={ref}
      className={`relative flex items-center justify-center ${className}`}
    >
      {inView && (
        <>
          <Image
            src={addQueryParams(backgroundSrc, { w: "600", h: "754" })}
            alt="Background pattern"
            width={750}
            height={942}
            className="absolute w-[500px] opacity-60 sm:w-[600px]"
            srcSet={`
          ${addQueryParams(backgroundSrc, { w: "464" })} 464w,
          ${addQueryParams(backgroundSrc, { w: "560" })} 560w,
          ${addQueryParams(backgroundSrc, { w: "552" })} 552w,
          ${addQueryParams(backgroundSrc, { w: "600" })} 600w
        `}
            sizes="(max-width: 640px) 560px, (max-width: 768px) 464px, (max-width: 1024px) 464px, (max-width: 1440px) 552px, 600px"
            fetchPriority="low"
            loading={ImageLoading.LAZY}
          />
          <motion.div
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0.5, y: 0, x: 0, scale: 1 },
              visible: {
                opacity: 1,
                x: screenSize === "desktop" ? "-100px" : "-50px",
                y: screenSize === "desktop" ? "-50px" : "-30px",
                scale: 1,
                transition: { duration: 1, ease: "linear" },
              },
            }}
            className="absolute z-10"
          >
            <Image
              src={addQueryParams(leftMobileSrc, { w: "483", h: "833" })}
              alt="Left mobile"
              width={483}
              height={833}
              className="drop-shadow-xl"
              srcSet={`
            ${addQueryParams(leftMobileSrc, { w: "464", h: "800" })} 464w,
            ${addQueryParams(leftMobileSrc, { w: "483", h: "833" })} 483w
          `}
              sizes="(max-width: 768px) 483px, (max-width: 1024px) 464px, 483px"
              fetchPriority="low"
              loading={ImageLoading.LAZY}
            />
          </motion.div>
          <motion.div
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 1, y: 0, x: 0, scale: 1 },
              visible: {
                opacity: 1,
                x: screenSize === "desktop" ? "100px" : "50px",
                y: 0,
                scale: 1,
                transition: { duration: 1, ease: "linear" },
              },
            }}
            className="absolute z-20"
          >
            <Image
              src={addQueryParams(rightMobileSrc, { w: "483", h: "796" })}
              alt="Right mobile"
              width={483}
              height={796}
              className="drop-shadow-xl"
              srcSet={`
            ${addQueryParams(rightMobileSrc, { w: "464", h: "800" })} 464w,
            ${addQueryParams(rightMobileSrc, { w: "483", h: "833" })} 483w
          `}
              sizes="(max-width: 768px) 483px, (max-width: 1024px) 464px, 483px"
              fetchPriority="low"
              loading={ImageLoading.LAZY}
            />
          </motion.div>
        </>
      )}
    </div>
  );
};

export default AnimatedMobileShowcase;
